/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { getId } from '@fluentui/react/lib/Utilities';
import { autorun, IReactionDisposer } from 'mobx';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getEventBus } from 'src/core/utils/eventBus';
import { modelTraversalHelpers } from 'src/core/utils/storeTreeTraversalHelpers/modelTraversalHelpers';

const eventBus = getEventBus();
const messages = defineMessages({
    collecting: {
        id: 'portal.trainingToastContent.collecting',
        defaultMessage: 'Collecting training data ...'
    },
    failTitle: {
        id: 'portal.useTrainingToastTracker.failed',
        defaultMessage: 'Train failed'
    },
    failDetails: {
        id: 'portal.useTrainingToastTracker.failedDetails',
        defaultMessage: 'Training failed: { reason }'
    },
    failDefaultReason: {
        id: 'portal.useTrainingToastTracker.defaultTrainFailReason',
        defaultMessage: 'An unknown error ocurred.'
    },
    inProgressTitle: {
        id: 'portal.useTrainingToastTracker.inProgressTitle',
        defaultMessage: 'Training app ...'
    },
    inProgress: {
        id: 'portal.trainingToastContent.inProgress',
        defaultMessage: 'In progress ...'
    },
    successTitle: {
        id: 'portal.useTrainingToastTracker.completed',
        defaultMessage: 'Train completed'
    },
    queued: {
        id: 'portal.trainingToastContent.queued',
        defaultMessage: 'Queued for training ...'
    },
    uploading: {
        id: 'portal.trainingToastContent.uploading',
        defaultMessage: 'Finalizing training ...'
    },
    success: {
        id: 'portal.trainingToastContent.success',
        defaultMessage: 'Finished training. Your changes are now effective.'
    },
    upToDate: {
        id: 'portal.trainingToastContent.upToDate',
        defaultMessage: 'Training is up to date.'
    }
});

/**
 * Creates a hook that reacts to the current training
 * status to create toasts that track the current
 * training progress.
 *
 * This handles the different application types, whether
 * conversation or documents.
 */
export const useLuisTrainingToastTracker = () => {
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const toastIdRef = React.useRef<string>();
    const autorunDisposerRef = React.useRef<IReactionDisposer>();

    const trainingStore = rootStore.currentLuisAppStore.currentAppVersionStore?.trainingStore;

    React.useEffect(() => () => {
            autorunDisposerRef.current?.();
            if (toastIdRef.current) {
                dispatcher.dispatch('removeToast', { id: toastIdRef.current });
            }
        }, []);

    React.useEffect(() => {
        // App or app version was not yet initialized.
        if (!trainingStore) {
            return;
        }

        /**
         * Cleanup any existing toast when training service
         * changes status to being ready.
         */
        if (trainingStore.serviceStatus === 'ready') {
            if (autorunDisposerRef.current) {
                setTimeout(() => {
                    toastIdRef.current = undefined;
                    autorunDisposerRef.current();
                }, 0);
            }
        } else {
            toastIdRef.current = getId();

            eventBus.emit('addToast', {
                id: toastIdRef.current,
                title: localize(messages.inProgressTitle),
                content: localize(messages.queued)
            });

            autorunDisposerRef.current = autorun(() => {
                if (!trainingStore.currentTrainingStatus) {
                    return;
                }

                /**
                 * In the case where the toast was dismissed by the
                 * user, this avoids undefined reference access.
                 */
                if (rootStore.toastStoreHouse.findById(toastIdRef.current) === undefined) {
                    return;
                }

                switch (trainingStore.currentTrainingStatus.overallStatus) {
                    case 'collectingData':
                        eventBus.emit('updateToast', {
                            id: toastIdRef.current,
                            toast: { title: localize(messages.inProgress), content: localize(messages.collecting), status: 'running' }
                        });
                        break;
                    case 'fail':
                        const { failedModel } = trainingStore.currentTrainingStatus;
                        let errorMessage: string;

                        if (failedModel) {
                            const modelName = modelTraversalHelpers(rootStore).getModelByIdFromCurrentApp(failedModel?.id)?.name;
                            errorMessage = `${modelName}: ${failedModel.error}`;
                        } else {
                            errorMessage = trainingStore.currentTrainingStatus.trainingFailureReason ?? localize(messages.failDefaultReason);
                        }

                        eventBus.emit('updateToast', {
                            id: toastIdRef.current,
                            toast: {
                                title: localize(messages.failTitle),
                                content: localize(messages.failDetails, { reason: errorMessage }),
                                status: 'failed'
                            }
                        });
                        break;
                    case 'inProgress':
                        const status = trainingStore.currentTrainingStatus;
                        const percentComplete = status.trainedModelsCount / status.totalModelsCount;
                        const progressIndicatorDescription = !isNaN(percentComplete) ? `${Math.round(percentComplete * 100)}%` : '';

                        eventBus.emit('updateToast', {
                            id: toastIdRef.current,
                            toast: {
                                title: localize(messages.inProgressTitle),
                                content: (
                                    <ProgressIndicator
                                        label={localize(messages.inProgress)}
                                        percentComplete={percentComplete}
                                        description={progressIndicatorDescription}
                                    />
                                ),
                                status: 'running'
                            }
                        });
                        break;
                    case 'queued':
                        eventBus.emit('updateToast', {
                            id: toastIdRef.current,
                            toast: { title: localize(messages.inProgressTitle), content: localize(messages.queued), status: 'running' }
                        });
                        break;
                    case 'success':
                        eventBus.emit('updateToast', {
                            id: toastIdRef.current,
                            toast: { title: localize(messages.successTitle), content: localize(messages.success), status: 'completed' }
                        });
                        break;
                    case 'upToDate':
                        eventBus.emit('updateToast', {
                            id: toastIdRef.current,
                            toast: { title: localize(messages.successTitle), content: localize(messages.upToDate), status: 'completed' }
                        });
                        break;
                    case 'uploadingModel':
                        eventBus.emit('updateToast', {
                            id: toastIdRef.current,
                            toast: { title: localize(messages.inProgressTitle), content: localize(messages.uploading), status: 'running' }
                        });
                        break;

                    default:
                }
            });
        }
    }, [trainingStore?.serviceStatus]);
};
